import { useState } from "react";
import { Box, Typography, Button, TextField, CircularProgress, Alert, Link, IconButton, InputAdornment } from "@mui/material";
import ContactModal from "../landing/ContactModal";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { useNavigate } from "react-router-dom";
import { APIURL } from "../../globals.jsx";
import { useAuth } from "../../contexts/AuthContext";

const GOLD      = "#D97706";
const GOLD_GRAD = "linear-gradient(135deg,#D97706,#F59E0B)";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 14,
    borderRadius: "10px",
    background: "#F8FAFC",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
    "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 14,
    color: "#64748B",
    "&.Mui-focused": { color: GOLD },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 12,
  },
};

const TRUST_POINTS = [
  "FATF-aligned AML compliance platform",
  "Purpose-built for UAE DNFBPs",
  "Real-time PEP & sanctions screening",
  "FIU-ready regulatory reporting",
];

export default function LoginScreen() {
  const navigate    = useNavigate();
  const { refetch } = useAuth();

  /* view: "login" | "forgot" | "forgot-sent" */
  const [modalOpen,  setModalOpen]  = useState(false);
  const [view,       setView]       = useState("login");
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [showPass,   setShowPass]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [forgotEmail,setForgotEmail]= useState("");
  const [forgotErr,  setForgotErr]  = useState("");
  const [forgotLoad, setForgotLoad] = useState(false);

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) { setError("Email is required."); return; }
    if (!password) { setError("Password is required."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${APIURL}/general/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Invalid email or password.");
      }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      await refetch();
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Forgot password ── */
  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotErr("");
    if (!forgotEmail) { setForgotErr("Email is required."); return; }
    setForgotLoad(true);
    try {
      const res = await fetch(`${APIURL}/general/forgot-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.error) throw new Error(data.error);
      setView("forgot-sent");
    } catch (err) {
      setForgotErr(err.message || "Something went wrong. Please try again.");
    } finally {
      setForgotLoad(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", fontFamily: "IBM Plex Sans, sans-serif" }}>
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* ── Left panel — branding ── */}
      <Box sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: "44%",
        background: "#0F172A",
        p: 6,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Ambient glow */}
        <Box sx={{ position: "absolute", top: "-10%", right: "-10%", width: "70%", height: "60%", background: "radial-gradient(ellipse, rgba(217,119,6,.12) 0%, transparent 65%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "0%", left: "-5%",  width: "60%", height: "50%", background: "radial-gradient(ellipse, rgba(37,99,235,.07) 0%, transparent 65%)",  pointerEvents: "none" }} />
        {/* Dot grid */}
        <Box sx={{ position: "absolute", inset: 0, opacity: .03, backgroundImage: "radial-gradient(rgba(255,255,255,.9) 1px, transparent 1px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />

        {/* Logo */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 42, display: "block", filter: "brightness(0) invert(1)" }} />
        </Box>

        {/* Center copy */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ fontSize: 32, fontWeight: 700, color: "#F1F5F9", lineHeight: 1.2, letterSpacing: "-1px", mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
            Your compliance
            <br />
            <Box component="span" sx={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              command centre.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748B", lineHeight: 1.8, mb: 5, fontFamily: "IBM Plex Sans, sans-serif" }}>
            Manage KYC, screen against 200+ sanctions lists, and generate FIU-ready reports — all in one place.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {TRUST_POINTS.map(p => (
              <Box key={p} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: GOLD, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: "#94A3B8", fontFamily: "IBM Plex Sans, sans-serif" }}>{p}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ fontSize: 12, color: "#334155", fontFamily: "IBM Plex Sans, sans-serif" }}>
            © {new Date().getFullYear()} AMLDiligence.com · ZAHADS Chartered Accountants
          </Typography>
        </Box>
      </Box>

      {/* ── Right panel — form ── */}
      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "#fff",
        px: { xs: 3, sm: 6, md: 10 },
        py: 6,
      }}>

        {/* Mobile logo */}
        <Box sx={{ display: { xs: "flex", lg: "none" }, mb: 6 }}>
          <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 38, display: "block" }} />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 400 }}>

          {/* ─── Login form ─── */}
          {view === "login" && (
            <>
              <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#0F172A", mb: .75, fontFamily: "IBM Plex Sans, sans-serif", letterSpacing: "-.5px" }}>
                Sign in
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748B", mb: 4, fontFamily: "IBM Plex Sans, sans-serif" }}>
                Enter your credentials to access the platform.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: "10px", fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleLogin} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required fullWidth size="small"
                  autoComplete="email"
                  sx={FIELD_SX}
                />
                <TextField
                  label="Password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required fullWidth size="small"
                  autoComplete="current-password"
                  sx={FIELD_SX}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPass(v => !v)}
                          edge="end"
                          size="small"
                          aria-label={showPass ? "Hide password" : "Show password"}
                          sx={{ color: "#94A3B8" }}
                        >
                          {showPass
                            ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                            : <VisibilityOutlinedIcon   sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => { setView("forgot"); setError(""); }}
                    underline="none"
                    sx={{ fontSize: 13, color: GOLD, fontFamily: "IBM Plex Sans, sans-serif", fontWeight: 500, "&:hover": { color: "#B45309" }, transition: "color .15s", cursor: "pointer" }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  endIcon={loading
                    ? <CircularProgress size={15} color="inherit" />
                    : <LockOutlinedIcon sx={{ fontSize: 17 }} />}
                  sx={{
                    py: 1.6, borderRadius: "10px",
                    fontSize: 15, fontWeight: 600, textTransform: "none",
                    fontFamily: "IBM Plex Sans, sans-serif",
                    background: GOLD_GRAD, color: "#fff",
                    boxShadow: "0 6px 20px rgba(217,119,6,.3)",
                    "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)", boxShadow: "0 10px 28px rgba(217,119,6,.45)" },
                    "&.Mui-disabled": { background: "rgba(217,119,6,.25)", color: "rgba(255,255,255,.5)" },
                    transition: "all .22s",
                  }}
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </Box>

              <Typography sx={{ mt: 4, fontSize: 12, color: "#94A3B8", textAlign: "center", fontFamily: "IBM Plex Sans, sans-serif" }}>
                Need access?{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => setModalOpen(true)}
                  underline="none"
                  sx={{ color: GOLD, fontWeight: 500, cursor: "pointer", fontFamily: "IBM Plex Sans, sans-serif", fontSize: 12, "&:hover": { color: "#B45309" } }}
                >
                  Contact us
                </Link>
              </Typography>
            </>
          )}

          {/* ─── Forgot password form ─── */}
          {view === "forgot" && (
            <>
              <Button
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                onClick={() => { setView("login"); setForgotErr(""); }}
                sx={{ mb: 3, fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13, fontWeight: 500, textTransform: "none", color: "#64748B", pl: 0, "&:hover": { color: "#0F172A", background: "none" } }}
              >
                Back to sign in
              </Button>

              <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#0F172A", mb: .75, fontFamily: "IBM Plex Sans, sans-serif", letterSpacing: "-.5px" }}>
                Reset your password
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748B", mb: 4, fontFamily: "IBM Plex Sans, sans-serif" }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {forgotErr && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: "10px", fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13 }}>
                  {forgotErr}
                </Alert>
              )}

              <Box component="form" onSubmit={handleForgot} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="Email address"
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  required fullWidth size="small"
                  autoComplete="email"
                  sx={FIELD_SX}
                />
                <Button
                  type="submit"
                  disabled={forgotLoad}
                  fullWidth
                  endIcon={forgotLoad ? <CircularProgress size={15} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    py: 1.6, borderRadius: "10px",
                    fontSize: 15, fontWeight: 600, textTransform: "none",
                    fontFamily: "IBM Plex Sans, sans-serif",
                    background: GOLD_GRAD, color: "#fff",
                    boxShadow: "0 6px 20px rgba(217,119,6,.3)",
                    "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)", boxShadow: "0 10px 28px rgba(217,119,6,.45)" },
                    "&.Mui-disabled": { background: "rgba(217,119,6,.25)", color: "rgba(255,255,255,.5)" },
                    transition: "all .22s",
                  }}
                >
                  {forgotLoad ? "Sending…" : "Send reset link"}
                </Button>
              </Box>
            </>
          )}

          {/* ─── Forgot password — sent ─── */}
          {view === "forgot-sent" && (
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(22,163,74,.1)", border: "1px solid rgba(22,163,74,.2)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: "#16A34A" }} />
              </Box>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>
                Check your inbox
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748B", mb: 4, lineHeight: 1.75, fontFamily: "IBM Plex Sans, sans-serif" }}>
                If <strong>{forgotEmail}</strong> is registered, a password reset link has been sent. It expires in 1 hour.
              </Typography>
              <Button
                onClick={() => { setView("login"); setForgotEmail(""); }}
                fullWidth
                sx={{
                  py: 1.5, borderRadius: "10px",
                  fontSize: 14, fontWeight: 600, textTransform: "none",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  border: "1px solid #E2E8F0", color: "#475569",
                  "&:hover": { background: "#F8FAFC", borderColor: "#94A3B8", color: "#0F172A" },
                  transition: "all .2s",
                }}
              >
                Back to sign in
              </Button>
            </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
}
